<template>
  <FormMainContainer
    v-if="answer.length > 0 && questionSentence.length > 0"
    :title="questionSentence"
    :breadcrumb="breadcrumb"
    :previous-step-route="previousStepRoute"
  >
    <template #title>
      <TitleSection :title="questionSentence" :previous-step-route="previousStepRoute">
        <template #leftNav>
          <Popper
            v-if="hasPreviousQuestion"
            :content="$t('kb.navigation.previous')"
            hover
            arrow
            class="fs-5 align-self-center me-1"
            placement="bottom"
            theme="light"
          >
            <span
              class="fs-3 text-decoration-underline fw-light cursor-pointer d-inline-flex align-items-center"
              @click.prevent="goToPreviousQuestion()"
            >
              <AppIcon icon="previous" />
            </span>
          </Popper>
          <Popper
            v-if="hasNextQuestion"
            :content="$t('kb.navigation.next')"
            hover
            arrow
            class="fs-5 align-self-center me-3"
            placement="bottom"
            theme="light"
          >
            <span
              class="fs-3 text-decoration-underline fw-light cursor-pointer d-inline-flex align-items-center"
              @click.prevent="goToNextQuestion()"
            >
              <AppIcon icon="next" />
            </span>
          </Popper>
        </template>
      </TitleSection>
    </template>
    <KbAnswer v-if="question" :lang="lang" :answer="answer" :question="question" />
  </FormMainContainer>
  <FormMainContainer
    v-else
    :breadcrumb="breadcrumb"
    :previous-step-route="homeUrl"
    :title="otherLocalizedQuestionSentence"
  >
    <div
      class="unavailable-selection-notification unavailable-selection-notification--adviser container-x-padding"
    >
      <div class="unavailable-selection-notification__icon">
        <AppIcon icon="warning" />
      </div>
      <div class="unavailable-selection-notification__text text-lg-nowrap">
        <p class="my-3 fs-5">
          {{ $t('kb.no_answer') }}
        </p>
      </div>
    </div>
  </FormMainContainer>
</template>

<script lang="ts">
import { defineComponent, PropType, nextTick } from 'vue'
import FormMainContainer from '~concierge/components/FormMainContainer.vue'
import TitleSection from '~concierge/components/TitleSection.vue'
import HeaderSection from '~concierge/components/HeaderSection.vue'
import FooterSection from '~concierge/components/FooterSection.vue'
import KbAnswer from '~conciergeUiCommons/components/KnowledgeDatabase/KbAnswer.vue'
import { KbQAData, KbLocalization, BreadcrumbResource } from '~types/concierge'
import { router, usePage } from '@inertiajs/vue3'
import { wTrans } from 'laravel-vue-i18n'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'

export default defineComponent({
  components: { TitleSection, HeaderSection, FooterSection, KbAnswer, FormMainContainer },
  setup() {
    const page = usePage()
    const homeUrl = page.props.homeUrl as string
    const submissionFormStore = useSubmissionFormStore()

    return {
      homeUrl,
      submissionFormStore
    }
  },
  created() {
    if (!this.question) {
      this.goHome()
    }
  },
  props: {
    question: {
      type: Object as PropType<KbQAData>,
      required: true
    }
  },
  computed: {
    lang(): string {
      return this.submissionFormStore.formLanguage ?? 'fr'
    },
    answer(): string {
      return this.question?.answer[this.lang as keyof KbLocalization] ?? ''
    },
    questionSentence(): string {
      return this.question?.question[this.lang as keyof KbLocalization] ?? ''
    },
    otherLocalizedQuestionSentence(): string {
      const locale = this.lang === 'fr' ? 'en' : 'fr'
      return (
        this.question?.question[locale as keyof KbLocalization] ??
        wTrans('kb.question_not_found').value
      )
    },
    breadcrumb(): { [key: number]: BreadcrumbResource } {
      if (this.question) {
        return this.question.breadcrumb[this.lang as keyof KbLocalization]
      }

      return []
    },
    previousQuestion(): string {
      return this.question?.endpoints.previous[this.lang as keyof KbLocalization] ?? ''
    },
    nextQuestion(): string {
      return this.question?.endpoints.next[this.lang as keyof KbLocalization] ?? ''
    },
    hasPreviousQuestion(): boolean {
      return this.previousQuestion.length > 0
    },
    hasNextQuestion(): boolean {
      return this.nextQuestion.length > 0
    },
    previousStepRoute(): string {
      return this.question?.endpoints.questions[this.lang as keyof KbLocalization] ?? ''
    }
  },
  methods: {
    async goHome(): Promise<void> {
      await nextTick()
      router.visit(this.homeUrl)
    },
    goToNextQuestion(): void {
      if (this.hasNextQuestion) {
        return router.visit(this.nextQuestion)
      }
    },
    goToPreviousQuestion(): void {
      if (this.hasPreviousQuestion) {
        return router.visit(this.previousQuestion)
      }
    }
  }
})
</script>
